import { FeatureFlagsContext } from '@tableau/data-catalog-shared';
import { KeySet } from '@tableau/data-structures';
import * as React from 'react';
import { Query } from 'react-apollo';
import {
  WorkbookLineageCountsFilteredByFieldQuery,
  WorkbookLineageCountsFilteredByFieldQueryVariables,
} from '../../../gen-src/queries/WorkbookLineageCountsFilteredByFieldQuery';
import {
  WorkbookLineageCountsQuery,
  WorkbookLineageCountsQueryVariables,
} from '../../../gen-src/queries/WorkbookLineageCountsQuery';
import {
  WorkbookLineageCountsFilteredByFieldQueryGql,
} from '../../queries/WorkbookLineageCountsFilteredByFieldQueryGql';
import { WorkbookLineageCountsQueryGql } from '../../queries/WorkbookLineageCountsQueryGql';
import { LineageCountsParentProps } from '../lineage/LineageCountsCacher';
import { renderFilteredLineageCounts, renderUnfilteredLineageCounts } from '../lineage/LineageCountsFunctions';

export type WorkbookLineageCounts = {
  readonly filteredFieldName: string | null;
  readonly upstreamDatabases: number;
  readonly upstreamTables: number;
  readonly upstreamVirtualConnections: number;
  readonly upstreamVirtualConnectionTables: number;
  readonly upstreamFlows: number;
  readonly upstreamDatasources: number;
  readonly downstreamSheets: number;
  readonly downstreamDashboards: number;
  readonly downstreamMetrics: number;
  readonly downstreamOwners: number;
};

function createUnfilteredCounts(data: WorkbookLineageCountsQuery): WorkbookLineageCounts | null {
  const workbook = data.workbooks && data.workbooks[0];
  if (workbook
    && workbook.upstreamDatabasesConnection
    && workbook.upstreamTablesConnection
    && workbook.upstreamFlowsConnection
    && workbook.upstreamDatasourcesConnection
    && workbook.sheetsConnection
    && workbook.dashboardsConnection
    && workbook.downstreamMetricsConnection
    && workbook.downstreamOwnersConnection
  ) {
    return {
      filteredFieldName: null,
      upstreamDatabases: workbook.upstreamDatabasesConnection.totalCount,
      upstreamTables: workbook.upstreamTablesConnection.totalCount,
      upstreamVirtualConnections: workbook.upstreamVirtualConnectionsConnection?.totalCount || 0,
      upstreamVirtualConnectionTables: workbook.upstreamVirtualConnectionTablesConnection?.totalCount || 0,
      upstreamFlows: workbook.upstreamFlowsConnection.totalCount,
      upstreamDatasources: workbook.upstreamDatasourcesConnection.totalCount,
      downstreamSheets: workbook.sheetsConnection.totalCount,
      downstreamDashboards: workbook.dashboardsConnection.totalCount,
      downstreamMetrics: workbook.downstreamMetricsConnection.totalCount,
      downstreamOwners: workbook.downstreamOwnersConnection.totalCount
    };
  }
  return null;
}

function createFilteredCounts(data: WorkbookLineageCountsFilteredByFieldQuery): WorkbookLineageCounts | null {
  const workbook = data.workbooks && data.workbooks[0];
  const embeddedDatasource = workbook && workbook.embeddedDatasources && workbook.embeddedDatasources[0];
  const filteredField = embeddedDatasource && embeddedDatasource.filteredFieldsConnection &&
    embeddedDatasource.filteredFieldsConnection.nodes[0];
  if (filteredField
    && filteredField.upstreamDatabasesConnection
    && filteredField.upstreamTablesConnection
    && filteredField.upstreamFlowsConnection
    && filteredField.upstreamDatasourcesConnection
    && filteredField.sheetsConnection
    && filteredField.dashboardsConnection
    && filteredField.downstreamMetricsConnection
    && filteredField.downstreamOwnersConnection
  ) {
    return {
      filteredFieldName: filteredField?.name ?? null,
      upstreamDatabases: filteredField.upstreamDatabasesConnection.totalCount,
      upstreamTables: filteredField.upstreamTablesConnection.totalCount,
      upstreamVirtualConnections: filteredField.upstreamVirtualConnectionsConnection?.totalCount || 0,
      upstreamVirtualConnectionTables: filteredField.upstreamVirtualConnectionTablesConnection?.totalCount || 0,
      upstreamFlows: filteredField.upstreamFlowsConnection.totalCount,
      upstreamDatasources: filteredField.upstreamDatasourcesConnection.totalCount,
      downstreamSheets: filteredField.sheetsConnection.totalCount,
      downstreamDashboards: filteredField.dashboardsConnection.totalCount,
      downstreamMetrics: filteredField.downstreamMetricsConnection.totalCount,
      downstreamOwners: filteredField.downstreamOwnersConnection.totalCount,
    };
  }
  return null;
}

export type WorkbookLineageCountsQueryWrapperProps = {
  readonly vizportalUrlId: string;
  readonly selectedEmbeddedDatasourceId: string;
  readonly selectedKeys: KeySet;
} & LineageCountsParentProps<WorkbookLineageCounts>;

export const WorkbookLineageCountsQueryWrapper = (props: WorkbookLineageCountsQueryWrapperProps) => {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);
  const keys = Object.keys(props.selectedKeys);
  if (keys.length > 0) {
    const selectedFieldId = keys[0];
    return (
      <Query<WorkbookLineageCountsFilteredByFieldQuery, WorkbookLineageCountsFilteredByFieldQueryVariables>
        query={WorkbookLineageCountsFilteredByFieldQueryGql}
        variables={{
          workbookVizportalUrlId: props.vizportalUrlId,
          selectedFieldId: selectedFieldId,
          selectedEmbeddedDatasourceId: props.selectedEmbeddedDatasourceId,
          publishedConnectionsLineageEnabled
        }}
      >
        {result => renderFilteredLineageCounts(result, createUnfilteredCounts, createFilteredCounts, props.children)}
      </Query>
    );
  } else {
    return (
      <Query<WorkbookLineageCountsQuery, WorkbookLineageCountsQueryVariables>
        query={WorkbookLineageCountsQueryGql}
        variables={{
          workbookVizportalUrlId: props.vizportalUrlId,
          publishedConnectionsLineageEnabled
        }}
      >
        {result => renderUnfilteredLineageCounts(result, createUnfilteredCounts, props.children)}
      </Query>
    );
  }
}
